/**
 * Instant feedback on add-to-cart.
 *
 * Client report 2026-09-08: "When I click Buy Now it takes quite some time to
 * redirect me to the checkout page. It's like I never pressed the button."
 *
 * Measured on dev: the button never changed at all, and the page did not leave
 * for 2-4 seconds. Most of that is Shopify's own `POST /cart/add` -> 302 ->
 * `GET /cart` chain, which the theme cannot shorten (the client's live Shopify
 * store is the same). What the theme CAN do is stop the click looking ignored,
 * which is the half the customer actually experiences.
 *
 * So this module does exactly one thing: on the click, before any network work,
 * put the clicked button into a visible "Adding..." state and leave it there
 * until the page unloads. Nothing here touches the cart, the form data, or the
 * submission itself.
 *
 * Covers all three buttons that add from a product page, because they all end
 * up submitting the same form:
 *   - "Buy Now"                          (submit button, sections/zager-product-main.liquid)
 *   - "See Total with Case and Shipping" (submit button, same form, has a formaction)
 *   - the sticky bar's "Add to cart"     (snippets/zager-sticky-atc.liquid ->
 *                                         zager-sticky-atc.js requestSubmit)
 * "Financing" is `type="button"` and opens a dialog, so it is deliberately not
 * matched.
 *
 * The busy button is NEVER given the `disabled` attribute. Disabling a
 * submitter inside its own click handler is the classic way to cancel the very
 * submission it was meant to confirm; `aria-disabled` plus a pointer-events
 * rule in the stylesheet tells assistive tech and blocks the second click
 * without going anywhere near form submission.
 */

/** Marks a button this module has taken over. */
const BUSY_ATTR = 'data-zg-atc-busy';

/** Original innerHTML per button, so a refused add can put the label back. */
const originalMarkup = new WeakMap();

/**
 * Localised copy, rendered by `snippets/zager-atc-busy.liquid`. Read from the
 * DOM on each use rather than cached, so theme-editor re-renders stay honest —
 * the same pattern as `zg-add-to-cart.js`. One querySelector and a small
 * JSON.parse; it does not measurably cost anything on the click path.
 *
 * @returns {string}
 */
function busyLabel() {
  const tag = document.querySelector('[data-zg-atc-busy-strings]');
  if (!tag?.textContent) return '';
  try {
    return JSON.parse(tag.textContent).adding || '';
  } catch {
    return '';
  }
}

/**
 * @param {HTMLElement} button
 */
function setBusy(button) {
  if (button.hasAttribute(BUSY_ATTR)) return;

  // Freeze the width BEFORE swapping the label. These buttons are sized by
  // their text, and "Buy Now" -> "Adding..." would otherwise resize the
  // control under the customer's cursor mid-click.
  const width = button.getBoundingClientRect().width;
  if (width) button.style.minWidth = `${width}px`;

  originalMarkup.set(button, button.innerHTML);
  button.setAttribute(BUSY_ATTR, '');
  button.setAttribute('aria-busy', 'true');
  button.setAttribute('aria-disabled', 'true');

  const spinner = document.createElement('span');
  spinner.className = 'zg-atc-busy__spinner';
  spinner.setAttribute('aria-hidden', 'true');

  const label = document.createElement('span');
  label.className = 'zg-atc-busy__label';
  // Fall back to the button's own label if the locale string is missing, so a
  // broken translation still leaves a readable button rather than a bare dot.
  label.textContent = busyLabel() || button.textContent.trim();

  button.replaceChildren(spinner, label);
}

/**
 * @param {HTMLElement} button
 */
function clearBusy(button) {
  if (!button.hasAttribute(BUSY_ATTR)) return;
  const markup = originalMarkup.get(button);
  if (markup != null) button.innerHTML = markup;
  originalMarkup.delete(button);
  button.removeAttribute(BUSY_ATTR);
  button.removeAttribute('aria-busy');
  button.removeAttribute('aria-disabled');
  button.style.minWidth = '';
}

function clearAllBusy() {
  for (const button of document.querySelectorAll(`[${BUSY_ATTR}]`)) {
    if (button instanceof HTMLElement) clearBusy(button);
  }
}

/**
 * The add-to-cart control a click landed on, or null.
 *
 * @param {Event} event
 * @returns {HTMLElement | null}
 */
function addToCartControl(event) {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return null;

  const sticky = target.closest('[data-zager-sticky-add]');
  if (sticky instanceof HTMLElement) return sticky;

  const button = target.closest('button, input[type="submit"]');
  if (!(button instanceof HTMLElement)) return null;
  // A <button> with no type inside a form is a submit button.
  const type = button.getAttribute('type');
  if (type && type !== 'submit') return null;
  // Horizon's own add-to-cart button adds over AJAX, stays on the page and
  // runs its own added/loading animation. Taking it over here would replace
  // that with a spinner nothing ever clears.
  if (button.closest('add-to-cart-component')) return null;
  const form = button.closest('form[action*="/cart/add"]');
  return form ? button : null;
}

// Capture phase, so the paint is queued before any other handler gets a chance
// to start network work on the same click.
document.addEventListener(
  'click',
  (event) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const button = addToCartControl(event);
    if (!button) return;

    // Second click while the first add is still in flight. The page has not
    // navigated yet, so the control is still reachable by keyboard even though
    // the stylesheet has taken pointer events away.
    if (button.hasAttribute(BUSY_ATTR)) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    setBusy(button);
  },
  { capture: true }
);

// Keyboard submits (Enter in the form) and any programmatic requestSubmit that
// names a submitter still get the state, without a click ever happening.
document.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!/\/cart\/add/.test(form.getAttribute('action') || '')) return;
    const submitter = event.submitter;
    if (!(submitter instanceof HTMLElement)) return;
    if (submitter.closest('add-to-cart-component')) return;
    setBusy(submitter);
  },
  { capture: true }
);

/**
 * An add that is refused rather than performed has to give the button back —
 * the one-per-cart guard in `zg-add-to-cart.js` fires this instead of
 * navigating.
 */
document.addEventListener('zager:atc:idle', clearAllBusy);

/**
 * Back/forward restores the page exactly as it was left, spinner and all. A
 * button that says "Adding..." forever on the back button is worse than the
 * problem this module fixes.
 */
window.addEventListener('pageshow', (event) => {
  if (event.persisted) clearAllBusy();
});
